import groq from "groq";
import { imageFragment } from "./image.queries";
import { leadFormProductionReadyFilter } from "./lead-form-runtime-resolution";

const versionIdCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

function compareLeadFormVersionIds(a: string, b: string): number {
  return versionIdCollator.compare(a, b);
}

function sortLeadFormVersionIds(versionIds: string[]): string[] {
  return [...versionIds].sort(compareLeadFormVersionIds);
}

const LeadFormVersionListQuery = groq`
  *[_type == "leadForm"]{ versionId, enabled } | order(versionId asc)
`;

const LeadFormEnabledVersionListQuery = groq`
  *[
    _type == "leadForm" &&
    enabled == true &&
    defined(versionId) &&
    !(_id in path("versions.**"))
  ]{
    versionId,
    internalName
  } | order(versionId asc)
`;

const leadFormByVersionFields = `
    versionId,
    formType,
    deliveryContext,
    enabled,
    stepTransition{
      enabled,
      variant,
      duration,
      delayBeforeExit
    },
    fullscreenTakeover{
      enabled
    },
    thankYou{
      icon,
      content[]{
        _key, _type,
        "buttonType": type,
        "buttonLabel": label,
        colorVariant,
        fontWeight,
        textSize,
        _type == "phoneLinkCta" => {
          icon,
          customIcon,
          widthVariant
        },
        _type == "richTextBlock" => {
          name,
          text,
          textAlignment,
          maxWidth
        },
        _type == "reviewStars" => {
          source,
          starVariant,
          reviewPlatform,
          reviewText,
          ariaLabel,
          contentAlignment,
          manualRating,
          manualReviewCount
        }
      }
    },
    steps[]{
      _key, _type, id, name, metricsLabel, showStepper,
      contentAlignment,
      contentAlignmentMobile,
      contentAlignmentTablet,
      contentAlignmentDesktop,
      contentMaxWidth,
      verticalPadding,
      _type == "interstitialStep" => {
        autoAdvance,
        autoAdvanceDelay,
        stepperDisplay{ variant, duration },
      },
      inputs[]{
        _key, _type,
        "buttonType": type,
        "buttonLabel": label,
        colorVariant,
        fontWeight,
        textSize,
        verticalPadding,
        _type == "formButton" => {
          widthVariant,
          validatesStep
        },
        _type == "phoneLinkCta" => {
          icon,
          customIcon,
          widthVariant
        },
        _type == "richTextBlock" => {
          name,
          text,
          textAlignment,
          maxWidth
        },
        _type == "radioInput" => {
          fieldName,
          helperText,
          helperTextSize,
          autoAdvance,
          selectionIndicator,
          maxWidth,
          options[]{
            _key, _type, label, value, description, icon, metricsLabel,
            "customIconUrl": customIcon.asset->url
          }
        },
        _type == "reviewStars" => {
          source,
          starVariant,
          reviewPlatform,
          reviewText,
          ariaLabel,
          contentAlignment,
          manualRating,
          manualReviewCount
        },
        _type == "standardInput" => {
          inputType,
          labelOverride,
          placeholderOverride
        },
        _type == "multiSelectInput" => {
          fieldName,
          label,
          buttonLabel,
          helperText,
          helperTextSize,
          variant,
          selectionIndicator,
          minSelected,
          maxSelected,
          options[]{
            _key, label, value, description, metricsLabel,
            "iconUrl": icon.asset->url
          }
        },
        _type == "contentGrid" => {
          name,
          columns[]{
            _key,
            name,
            items[]{
              _key, _type,
              _type == "iconItem" => {
                name,
                "iconUrl": icon.asset->url,
                caption,
                verticalPadding,
                animation
              },
              _type == "richTextBlock" => {
                name,
                text,
                textAlignment,
                maxWidth
              }
            }
          }
        }
      },
      infoPanel{
        mode,
        alignment,
        maxWidth,
        showFromBreakpoint,
        textAlignment,
        visibility,
        variant,
        showCaregiverCTA,
        heroImageLarge{ _type, ${imageFragment} },
        heroImageSmall{ _type, ${imageFragment} },
        items[]{
          _key, _type,
          _type == "richTextBlock" => { name, text, textAlignment },
          _type == "infoPanelIcon" => { icon, customIcon, size }
        }
      }
    }
`;

/**
 * Site-scoped lead-form resolver. See `lead-form-runtime-resolution.ts` for
 * the production-readiness filter shared with Studio validation.
 */
const LeadFormByVersionQuery = groq`
  *[_type == "leadForm" && versionId == $versionId && siteId == $siteId && ${leadFormProductionReadyFilter}][0]{${leadFormByVersionFields}}
`;

export {
  compareLeadFormVersionIds,
  LeadFormByVersionQuery,
  LeadFormEnabledVersionListQuery,
  LeadFormVersionListQuery,
  sortLeadFormVersionIds,
};
